'use client';

import type { AxiosRequestConfig } from 'axios';

import { type Brand } from '@/consts/brands';
import {
  type TreatmentCategory,
  TreatmentCategoryIds,
} from '@/consts/treatmentCategories';
import {
  type ActivateSubscriptionData,
  type RecalculateTaxData,
  type UpdateShippingAddressData,
} from '@/types/productSelectionAndCheckout/types';
import { type PurchaseContext } from '@/types/purchaseContextToggle/types';
import { get, patch, post, put } from '@/utils/http/client';
import { urls } from '@/utils/urls';

const activateTrial = (
  brand: Brand,
  shipmentFrequencyDays: number,
): Promise<ActivateSubscriptionData> =>
  post(urls[brand].api.productSelectionAndCheckout.activateTrial, {
    shipmentFrequency: shipmentFrequencyDays,
  });

const activateSubscription = (
  brand: Brand,
  treatmentCategory: TreatmentCategory,
): Promise<ActivateSubscriptionData> =>
  post(urls[brand].api.productSelectionAndCheckout.activateSubscription, {
    treatmentCategoryId: TreatmentCategoryIds[treatmentCategory],
  });

const getSmartyAutosuggestResults = (brand: Brand, search: string) =>
  get(urls[brand].api.smarty.getAutosuggestResults(search));

const getUpdatedProductSelectionData = (
  brand: Brand,
  treatmentCategory: TreatmentCategory,
  selectedItemIds: number[],
): Promise<RecalculateTaxData> =>
  get(
    urls[brand].api.productSelectionAndCheckout.getUpdatedProductSelectionData(
      treatmentCategory,
    ),
    {
      params: { selectedItemIds },
    },
  );

const setPatientViewedAt = (
  brand: Brand,
  treatmentCategory: TreatmentCategory,
) =>
  put(
    urls[brand].api.productSelectionAndCheckout.setPatientViewedAt(
      treatmentCategory,
    ),
  );

const updateProductSelectionContext = async (
  brand: Brand,
  purchaseContext: PurchaseContext,
) => {
  const data = { purchaseContext };
  await put(
    urls[brand].api.productSelectionAndCheckout.updatePurchaseContext,
    data,
  );
};

const updateSelectedItems = (
  brand: Brand,
  selectedItemIds: number[],
  treatmentCategory: string,
  config?: AxiosRequestConfig,
) => {
  const items = selectedItemIds.map((selectedItemId) => ({
    item_id: selectedItemId,
    quantity: 1,
  }));

  const data = {
    cancelTypeId: undefined,
    deferred_products: [],
    items,
    treatment_category: treatmentCategory,
  };

  return put(
    urls[brand].api.productSelectionAndCheckout.updateSelectedItems,
    data,
    config,
  );
};

const updateShippingAddress = (
  brand: Brand,
  data: {
    address1: string;
    address2: string;
    addressee_name: string;
    city: string;
    firstname: string;
    lastname: string;
    phoneNumberOptional: boolean;
    state: string;
    zipcode: string;
  },
): Promise<UpdateShippingAddressData> =>
  patch(
    urls[brand].api.productSelectionAndCheckout.updateShippingAddress,
    data,
  );

export const ProductSelectionAndCheckoutService = {
  activateTrial,
  activateSubscription,
  getSmartyAutosuggestResults,
  getUpdatedProductSelectionData,
  setPatientViewedAt,
  updateProductSelectionContext,
  updateSelectedItems,
  updateShippingAddress,
};
